import React from 'react'
import { useQuery, UseQueryOptions } from 'react-query'
import { compose, concat, equals, ifElse, indexBy, pathOr, pluck, prop, filter } from 'ramda'
import { useRecoilValue } from 'recoil'
import useComapnyCode from '@/hooks/useComapnyCode'
import useWebSystemRole from '@/hooks/useWebSystemRole'
import { history } from '@/routes'
import { approvalCompanyCode } from '../constants'
import { IOrganizationFull, IOrganizationDepartment } from './options.type'

/**
 * 綠版 帶 companySeq 必填
 * 藍版 帶 companyCode 必填
 */
export const useOrganizationFullList = (
  query?: { companySeq: number } & any,
  options?: UseQueryOptions<IOrganizationFull[]>
) => {
  // 綠版簽核
  const _approvalCompanyCode = useRecoilValue(approvalCompanyCode)
  const isApprovalPage = React.useMemo(
    () =>
      _approvalCompanyCode && history.location.pathname.indexOf('admin-approval-management') > -1,
    [_approvalCompanyCode, history.location.pathname]
  )
  const systemRole = useWebSystemRole()
  const companyCode = useComapnyCode({ supportSystemAdminCode: isApprovalPage ? false : true })

  const isEnabled: boolean = React.useMemo(() => {
    return Boolean(
      ifElse(
        () => Boolean(isApprovalPage),
        () => !!companyCode,
        () =>
          ifElse(
            () => equals('SystemAdmin', systemRole),
            () => Boolean(pathOr(undefined, ['companySeq'], query)),
            () => !!companyCode
          )()
      )()
    )
  }, [isApprovalPage, companyCode, systemRole, query?.companySeq])

  const { data, isLoading, refetch } = useQuery<IOrganizationFull[]>(
    ['/{companyCode}/organization/full', { companyCode, ...query }],
    {
      enabled: isEnabled,
      select: data => pathOr([], ['data'], data),
    }
  )

  const byId = React.useMemo(() => {
    return indexBy(prop('id'), data || [])
  }, [data])

  return { data, byId, isLoading, refetch }
}

/**
 * 綠版 帶 companySeq 必填
 * 藍版 帶 companyCode 必填
 */
export const useDepartmentFullList = (
  query?: any,
  options?: UseQueryOptions<IOrganizationFull[]>
) => {
  const companyCode = useComapnyCode({ supportSystemAdminCode: true })

  const { data, isLoading, refetch } = useQuery<IOrganizationFull[]>(
    ['/{companyCode}/organization/full', { companyCode, ...query }],
    {
      select: data => pathOr([], ['data'], data),
      enabled: companyCode === 'systemAdmin' ? !!query?.companySeq : !!companyCode,
      ...options,
    }
  )

  const dataSource = React.useMemo(() => {
    let result: IOrganizationDepartment[] = []
    const pluckDepartments = pluck('departments', data || [])
    pluckDepartments.forEach(pluckDepartment => {
      result = concat(result, pluckDepartment)
    })
    return result
  }, [data])

  const byId = React.useMemo(() => {
    return indexBy(prop('id'), dataSource)
  }, [dataSource])

  const showDepartmentWithOrganization = React.useCallback(
    (departmentId: number) => {
      const matchOrg = data?.filter(
        org => org.departments.filter(d => d.id === departmentId).length > 0
      )
      if (!matchOrg || matchOrg.length === 0) {
        return ''
      }
      return `${pathOr('', [0, 'shortName'], matchOrg)}/${compose<any, any, any, string>(
        pathOr('', [0, 'shortName']),
        filter((d: IOrganizationDepartment) => d.id === departmentId),
        pathOr([], [0, 'departments'])
      )(matchOrg)}`
    },
    [data]
  )

  return {
    dataSource,
    isLoading,
    byId,
    refetch,
    showDepartmentWithOrganization,
  }
}
